import { getAllCompositeSegments, getCompositeSegmentById } from '@/player-config/src/utils/clipUtils';
import { getDeletesForSegmentPure } from '@/player-config/src/utils/timeUtils';
import { getSegmentsTrack } from '@/player-config/src/utils/playerConfigGetters';
import { Segment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/Segment';
import { SegmentType } from '@/player-config/src/types/Segment/Type';
import { TrackType } from '@/player-config/src/types/Track/Type';
import { ElementsTrack } from '@/player-config/src/types/RemotionPlayerConfigTypes/Track/ElementsTrack';
import { CompositionConfig } from '@/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { Clip } from '@/player-config/src/types/clip-types';
import { isSplitsInTimelineEnabled } from '@/stores/clip';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

export const getCompositeSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    const segmentsTrack = clip.asset_metadata.player_config && getSegmentsTrack(clip.asset_metadata.player_config);
    return segmentsTrack?.segments?.find(segment => segment.type === SegmentType.COMPOSITE);
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    return getCompositeSegmentById(clip, segmentId);
  }
  return getAllCompositeSegments(clip)[0];
};

export const getDeletesForSegment = (clip: Clip, segmentId?: string) => {
  return getDeletesForSegmentPure({
    clip,
    segmentId,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });
};

export const getEditsForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled) {
    return clip.asset_metadata.edits;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.edits || {};
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.edits || {};
};

export const getMergesForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.merges;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.merges || [];
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.merges || [];
};

export const getExcludesForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.excludes;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.excludes || [];
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.excludes || [];
};

export const getMaxVideoDurationForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.maxVideoDuration;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.maxVideoDuration || 0;
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.maxVideoDuration || 0;
};

export const getFitForSegment = (segment: Segment, clip: Clip) => {
  if (segment.type !== SegmentType.COMPOSITE) {
    return undefined;
  }
  return (isSplitsInTimelineEnabled(clip) ? segment.segment_metadata?.fitMode : clip.asset_metadata.fit) || undefined;
};

export const getScenesForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;

  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.scenes;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.scenes || [];
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.scenes || [];
};

/**
 * Filters and repositions element tracks (overlays) for a newly created sub-clip.
 *
 * When creating a clip from a selection, the element tracks in the computed player_config
 * get corrupted by double processing in updateClipDataWithSplits. This function takes the
 * original element tracks and filters/adjusts them to the new clip's time range.
 *
 * @param playerConfig - The player_config from the computed clip (correct segment track)
 * @param originalPlayerConfig - The player_config from the original clip (correct element frames)
 * @param playerStartFrame - The start frame of the selection in the original clip's player timeline
 * @param playerEndFrame - The end frame of the selection in the original clip's player timeline
 */
export const filterAndAdjustElementTracksForSubClip = (
  playerConfig: CompositionConfig | null,
  originalPlayerConfig: CompositionConfig | null,
  playerStartFrame: number,
  playerEndFrame: number
): CompositionConfig | null => {
  if (!playerConfig || !originalPlayerConfig) {
    return playerConfig;
  }
  const updatedTracks = playerConfig.playerConfig.tracks.map(track => {
    if (track.type !== TrackType.ELEMENTS) {
      return track;
    }
    const originalElementsTrack = originalPlayerConfig.playerConfig.tracks.find(
      t => t.type === TrackType.ELEMENTS && (t as ElementsTrack).id === (track as ElementsTrack).id
    ) as ElementsTrack | undefined;
    const elements = originalElementsTrack?.elements ?? (track as ElementsTrack).elements ?? [];
    return {
      ...(track as ElementsTrack),
      elements: elements
        .filter(el => el.toFrame > playerStartFrame && el.fromFrame < playerEndFrame)
        .map(el => ({
          ...el,
          fromFrame: Math.max(0, el.fromFrame - playerStartFrame),
          toFrame: Math.min(playerEndFrame - playerStartFrame, el.toFrame - playerStartFrame)
        }))
    };
  });
  return {
    ...playerConfig,
    playerConfig: {
      ...playerConfig.playerConfig,
      tracks: updatedTracks
    }
  };
};

export const getDurationForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.duration;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.duration || 0;
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.duration || 0;
};
